<template>
  <div class="cashier-component">
    <loader v-if="isLoading" absolute />
    <div v-else-if="errorFetch" class="cashier-error">
      <v-alert color="red lighten-2 text-center" dark>
        {{ errorText }}
      </v-alert>
    </div>
    <template v-else>
      <v-row>
        <v-col sm="6" cols="12" align-self="center">
          <v-row align="center">
            <v-col cols="6">
              <h1 class="page-title">
                Касса
              </h1>
              <v-progress-linear
                v-if="shiftStatusLoading"
                style="max-width: 250px"
                indeterminate
              ></v-progress-linear>
              <div v-else class="shift-status-label">
                {{ shiftStatusLabel }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="top-buttons-container">
                <v-btn
                  v-if="userCanChangeShiftStatus"
                  class="top-btn"
                  color="primary"
                  :loading="shiftStatusLoading"
                  depressed
                  :large="$vuetify.breakpoint.lgAndUp"
                  @click="showFiscalShiftDialog"
                >
                  <template v-if="isFiscalShiftClosed">
                    <font-awesome-icon
                      size="lg"
                      class="mr-2"
                      icon="play-circle"
                    />
                    Открыть смену
                  </template>
                  <template v-else>
                    <font-awesome-icon
                      size="lg"
                      class="mr-2"
                      icon="stop-circle"
                    />
                    Закрыть смену
                  </template>
                </v-btn>
                <v-menu
                  v-if="isKkmServerPaymentEnabled"
                  v-model="showTerminalMenu"
                  offset-y
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="top-btn terminal-btn"
                      color="primary"
                      depressed
                      v-bind="attrs"
                      :large="$vuetify.breakpoint.lgAndUp"
                      v-on="on"
                    >
                      <font-awesome-icon
                        size="lg"
                        class="mr-2"
                        icon="credit-card"
                      />
                      Терминал
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-text style="padding: 8px">
                      <div class="d-flex flex-column">
                        <v-btn
                          dark
                          depressed
                          color="red"
                          class="mb-2"
                          :loading="isCardShiftClosing"
                          @click="closeCardShift"
                        >
                          Закрыть смену и сверить итоги
                        </v-btn>
                        <v-btn
                          :dark="terminalReportLoading !== 'full'"
                          depressed
                          color="#ff9100"
                          class="mb-2"
                          :loading="terminalReportLoading === 'short'"
                          :disabled="terminalReportLoading === 'full'"
                          @click="getTerminalReport(false)"
                        >
                          Итоги дня (краткий)
                        </v-btn>
                        <v-btn
                          :dark="terminalReportLoading !== 'short'"
                          depressed
                          color="#ff9100"
                          :loading="terminalReportLoading === 'full'"
                          :disabled="terminalReportLoading === 'short'"
                          @click="getTerminalReport(true)"
                        >
                          Итоги дня (полный)
                        </v-btn>
                        <div class="terminal-menu_hint">
                          Последняя сверка: {{ lastTerminalSettlementLabel }}
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col
          sm="6"
          cols="12"
          style="margin-bottom: -10px"
          align-self="center"
        >
          <div v-if="balance.debtorAlert" class="debtor-alert">
            <font-awesome-icon icon="exclamation-triangle" />
            <span>{{ balance.debtorAlert }}</span>
          </div>
          <div
            v-if="fiscalShiftWarning.show"
            class="debtor-alert red lighten-1"
          >
            <font-awesome-icon icon="exclamation-circle" />
            <span>{{ fiscalShiftWarning.text }}</span>
          </div>
          <div
            v-if="settlementNotice"
            class="debtor-alert"
            :class="{ 'red lighten-1': settlementNotice.isError }"
          >
            <font-awesome-icon :icon="settlementNotice.isError ? 'exclamation-circle' : 'sync-alt'" />
            <span>{{ settlementNotice.text }}</span>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col lg="6" cols="12" class="left-side col-xs-12">
          <v-row>
            <v-col sm="7" cols="12">
              <span class="label">Выручка:</span>
              <div class="stat-item lg icon stat-item-total-today">
                <icon-cashier />
                <span class="value income-today-value">{{
                  balanceProceed.proceedTotalToday | amountWithCurrency0
                }}</span>
                <span>Выручка за сегодня</span>
              </div>
            </v-col>
            <v-col sm="5" cols="12">
              <template v-if="cashBoxIsClosed">
                <span class="label">
                  Сейф, на конец смены
                  <span class="font-weight-medium">
                    {{ selectedDate }}:
                  </span>
                </span>
                <div class="stat-item lg">
                  <span class="value">
                    {{ endCashBalance | amountWithCurrency0 }}
                  </span>
                  <span>Наличные</span>
                </div>
              </template>

              <template v-else>
                <span class="label">
                  Сейф, остаток на
                  <span class="font-weight-medium">
                    {{ selectedDate }}:
                  </span>
                </span>
                <div class="stat-item lg">
                  <span class="value">
                    {{ currentCashBalance | amountWithCurrency0 }}
                  </span>
                  <span>Наличные</span>
                </div>
              </template>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="7" cols="12" class="d-flex flex-column">
              <span class="label">Источники:</span>
              <revenue-sources-list :balance-proceed="balanceProceed" />
            </v-col>
            <v-col sm="5" cols="12">
              <v-row style="height: 100%" dense>
                <v-col class="d-flex flex-column" sm="6" cols="12">
                  <span class="label">Инкасация:</span>
                  <div class="stat-item sm">
                    <span class="value">{{
                      balanceCollection.collectionCashSubtract
                        | amountWithCurrency0
                    }}</span>
                    <span>Наличные</span>
                  </div>
                </v-col>
                <v-col class="d-flex flex-column" sm="6" cols="12">
                  <span class="label">Пополнение:</span>
                  <div class="stat-item sm">
                    <span class="value">{{
                      balanceCollection.collectionCashAdd | amountWithCurrency0
                    }}</span>
                    <span>Наличные</span>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col lg="6" cols="12">
          <v-row>
            <v-col sm="9" cols="12" class="d-flex flex-column">
              <span class="label">Должники:</span>
              <debtors-table :debtors="itemsDebtors" />
            </v-col>
            <v-col sm="3" cols="12" class="debtor-totals-col">
              <span class="label">Должны в кассу:</span>
              <total-debt-info :info="balanceDebtorInfo" />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row v-if="connectorIndicators.length">
        <!--        <v-col-->
        <!--          sm="5"-->
        <!--          class="pb-0"-->
        <!--          cols="12"-->
        <!--          order-sm="1"-->
        <!--        >-->
        <!--          <v-row>-->
        <!--            <v-col sm="12" cols="12">-->
        <!--              <span class="label">Касса:</span>-->
        <!--              <div class="stat-item md">-->
        <!--                <span class="value">{{-->
        <!--                  balanceTotal.cashTotal | amountWithCurrency0-->
        <!--                }}</span>-->
        <!--                <span>наличные</span>-->
        <!--              </div>-->
        <!--            </v-col>-->
        <!--          </v-row>-->
        <!--        </v-col>-->
        <v-col cols="12">
          <span class="label">Коннекторы:</span>
          <article class="connector-indicators-wrapper">
            <connector-indicator-card
              v-for="(item, index) in connectorIndicators"
              :key="index"
              :item="item"
            />
          </article>
        </v-col>
      </v-row>
      <span class="label">История транзакций</span>
      <transaction-history-table :transactions="itemsTransactions" />
    </template>
    <primary-confirm-dialog
      v-model="shiftStatusDialog"
      :title="shiftStatusDialogLabel"
      :loading="shiftStatusLoading"
      close-after-loading
      @confirm="shouldOpenFiscalShift ? openFiscalShift() : closeFiscalShift()"
    >
      <template v-if="showShiftDialogSubtitle" #subtitle>
        <div v-if="fiscalShiftStatusDialogSubtitle">
          <font-awesome-icon class="mr-1" icon="exclamation-triangle" />
          {{ fiscalShiftStatusDialogSubtitle }}
        </div>
        <div v-if="showSettlementWarning" class="settlement-warning">
          <div>Сверка терминала за эту смену не проводилась, она запустится вместе с закрытием</div>
          <v-checkbox
            v-model="skipTerminalSettlement"
            class="settlement-warning_checkbox"
            label="Не сверять терминал"
            hide-details
            dense
          />
        </div>
      </template>
    </primary-confirm-dialog>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import IconCashier from '@/components/icons/IconCashier'
  import mixinSnackbarMessage from '@/mixins/snackbar-message'
  import Loader from '@/components/loader/Loader'
  import fiscalShiftApi from '@/api/administrator/fiscal-shift'
  import terminalSettlementApi from '@/api/administrator/terminal-settlement'
  import { autoSettlementNotice, watchSettlement } from '@/services/terminal-settlement'
  import terminalReportApi from '@/api/administrator/terminal-report'
  import { watchTerminalCommand } from '@/services/terminal-operation-poller'
  import moment from 'moment'
  import { formatIsoDateTime, formatIsoDateTimeAsDateTime, todayInTimeZone } from '@/util/date-time-helper'
  import FiscalShiftStatus from '@/models/enums/FiscalShiftStatus'
  import TransactionHistoryTable from '@/components/administrator/cashier/TransactionHistoryTable.vue'
  import DebtorsTable from '@/components/administrator/cashier/DebtorsTable.vue'
  import TotalDebtInfo from '@/components/administrator/cashier/TotalDebtInfo.vue'
  import RevenueSourcesList from '@/components/administrator/cashier/RevenueSourcesList.vue'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'
  import { websocketService } from '@/services/WebsocketService'
  import { getFilialChannel } from '@/util/websocket-channels'
  import { FISCAL_SHIFT_STATUS_CHANGED } from '@/constants/websocket-events'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'
  import ConnectorIndicatorCard from '@/components/administrator/cashier/ConnectorIndicatorCard.vue'
  import paymentApi from '@/api/order/payment'
  import PaymentType from '@/models/enums/PaymentType'

  export default {
    name: 'Cashier',
    components: {
      ConnectorIndicatorCard,
      PrimaryConfirmDialog,
      TotalDebtInfo,
      Loader,
      IconCashier,
      TransactionHistoryTable,
      DebtorsTable,
      RevenueSourcesList,
    },
    mixins: [mixinSnackbarMessage],
    props: {
      selectedDate: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        itemsDebtors: [],
        itemsTransactions: [],
        balance: {},
        isLoading: false,
        errorFetch: false,
        errorText: '',
        showFiscalShiftStatusWarning: false,
        fiscalShiftWarning: {
          show: false,
          text: '',
        },
        shiftStatusLoading: true,
        shiftStatusDialog: false,
        shiftPollTimer: null,
        shiftPollStartedAt: null,
        syncPollTimer: null,
        shouldOpenFiscalShift: false,
        fiscalShift: {
          status: FiscalShiftStatus.Closed,
          date: moment().format('YYYY-MM-DD'),
          changedAt: null,
          terminalSettlementRequired: false,
          lastTerminalSettlementAt: null,
        },
        skipTerminalSettlement: false,
        showTerminalMenu: false,
        paymentSettings: {},
        isPaymentTypesFetching: false,
        terminalReportLoading: null,
        terminalReportWatcher: null,
        isCardShiftClosing: false,
        settlementWatcher: null,
        settlementNotice: null,
      }
    },
    computed: {
      ...mapGetters({
        localStorageBranch: 'branches/getLocalStorageBranch',
        companyName: 'app/getCompanyName',
        isKkmServerProxyEnabled: 'printServer/isKkmServerProxyEnabled',
        filialId: 'branches/getGlobalFilialId',
      }),
      balanceCollection () {
        return this.balance.collection ?? {}
      },
      balanceProceed () {
        return this.balance.proceed ?? {}
      },
      connectorIndicators () {
        return this.balance?.proceed?.connectorIndicators ?? []
      },
      balanceTotal () {
        return this.balance.total ?? {}
      },
      balanceDebtorInfo () {
        return this.balance.debtorInfo ?? {}
      },
      balanceBagin () {
        return this.balance.begin ?? {}
      },
      itemsDebtorsFormatted () {
        return this.itemsDebtors.map((item) => {
          // item.totalBalance = this.$options.filters.currency(item.totalBalance)
          // item.cashBalance = this.$options.filters.currency(item.cashBalance)
          item.terminalBalance =
            this.$options.filters.currency(item.terminalBalance) +
            ' (' +
            item.checksCount +
            ')'
          return item
        })
      },
      isShiftStatusForCurrentDate () {
        return this.fiscalShift.date === this.filialToday()
      },
      isFiscalShiftClosed () {
        return this.fiscalShift.status === FiscalShiftStatus.Closed
      },
      shiftDateLabel () {
        const timeZone = this.localStorageBranch?.timeZone || null

        // changedAt - точный момент операции; фолбэк - дата смены без времени
        if (this.fiscalShift.changedAt) {
          return formatIsoDateTimeAsDateTime(
            this.fiscalShift.changedAt,
            null,
            timeZone,
          )
        }

        return formatIsoDateTimeAsDateTime(this.fiscalShift.date, null, timeZone)
      },
      shiftStatusLabel () {
        if (this.fiscalShift.status === FiscalShiftStatus.Closed) {
          return `Смена закрыта ${this.shiftDateLabel}`.trim()
        }

        return `Смена открыта с ${this.shiftDateLabel}`.trim()
      },
      shiftStatusDialogLabel () {
        return `Вы действительно хотите ${
          this.shouldOpenFiscalShift ? 'открыть смену' : 'закрыть смену'
        }?`
      },
      fiscalShiftStatusDialogSubtitle () {
        if (
          this.isShiftStatusForCurrentDate &&
          this.fiscalShift.status === FiscalShiftStatus.Open &&
          this.shouldOpenFiscalShift
        ) {
          return 'Смена уже открыта'
        }

        if (
          this.fiscalShift.status === FiscalShiftStatus.Closed &&
          !this.shouldOpenFiscalShift
        ) {
          return 'Смена уже закрыта'
        }

        if (!this.shouldOpenFiscalShift && !this.isShiftStatusForCurrentDate) {
          return `Смена будет закрыта за ${moment(this.fiscalShift.date).format(
            'DD.MM.YYYY',
          )}`
        }

        return null
      },
      showSettlementWarning () {
        return !this.shouldOpenFiscalShift && this.fiscalShift.terminalSettlementRequired
      },
      showShiftDialogSubtitle () {
        return Boolean(this.fiscalShiftStatusDialogSubtitle) || this.showSettlementWarning
      },
      lastTerminalSettlementLabel () {
        return formatIsoDateTimeAsDateTime(
          this.fiscalShift.lastTerminalSettlementAt,
          'не проводилась',
          this.localStorageBranch?.timeZone || null,
        )
      },
      userCanChangeShiftStatus () {
        return this.userCan(CRMPermission.WorkShift)
      },
      isKkmServerPaymentEnabled () {
        return this.isKkmServerProxyEnabled && Boolean(this.paymentSettings?.available)
      },
      cashBoxIsClosed () {
        return this.balance?.isClosed
      },
      endCashBalance () {
        return this.balance?.end?.endCash
      },
      currentCashBalance () {
        return this.balance?.total?.cashTotalToday
      },
    },
    watch: {
      selectedDate () {
        this.getBalance()
      },
    },
    mounted () {
      this.$root.$refs.cashierGetBalance = this.getBalance
      this.fetchAvailablePaymentTypes()
      this.getBalance()
      this.getShiftStatus(this.selectedDate)
      this.syncShiftState()

      websocketService
        .getEcho()
        .private(getFilialChannel(this.localStorageBranch.id))
        .listen(FISCAL_SHIFT_STATUS_CHANGED, this.updateShiftStatus)
    },
    beforeDestroy () {
      this.settlementWatcher && this.settlementWatcher.cancel()
      this.terminalReportWatcher && this.terminalReportWatcher.cancel()
      this.stopShiftPolling()
      this.stopSyncPolling()
      this.$root.$refs.cashierGetBalance = null

      websocketService
        .getEcho()
        .private(getFilialChannel(this.localStorageBranch.id))
        .stopListening(FISCAL_SHIFT_STATUS_CHANGED, this.updateShiftStatus)
    },
    methods: {
      async fetchAvailablePaymentTypes () {
        this.isPaymentTypesFetching = true

        try {
          const types = await paymentApi.fetchTypes(this.filialId)
          const type = types.find((item) => item.type === PaymentType.Terminal)
          this.paymentSettings = type?.settings || null
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          this.isPaymentTypesFetching = false
        }
      },
      getBalance () {
        this.errorFetch = false
        this.errorText = ''

        const params = {
          filialId: this.filialId,
          date: this.selectedDate,
        }

        this.isLoading = true
        this.$store
          .dispatch('admin/fetchBalance', params)
          .then((res) => {
            if (res.status === 200) {
              const data = res.data.data
              this.balance = data
              this.itemsTransactions = data.correctTransactionsList
              this.itemsDebtors = data.debtorList
            } else {
              this.errorFetch = true
              this.errorText = res.response.data.message
              // this.showErrorMessage(res.response.data.message)
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      },
      async getShiftStatus () {
        this.shiftStatusLoading = true
        try {
          const response = await fiscalShiftApi.fetchStatus(
            this.localStorageBranch.id,
          )
          this.applyShiftResponse(response)

          if (response.pending) {
            // операция запущена в другой вкладке/сессии - продолжаем ждать
            this.startShiftPolling()
            return
          }
        } catch (e) {
          this.showErrorFromResponse(e)
        } finally {
          if (!this.shiftPollTimer) {
            this.shiftStatusLoading = false
          }
        }
      },
      applyShiftResponse (response) {
        this.applyShiftState(response)
        this.applySettlementInfo(response)
      },
      applyShiftState (response) {
        // бек может отдавать число - нормализуем к строковому enum FiscalShiftStatus
        this.fiscalShift.status = String(response.status)
        this.fiscalShift.date = response.date
        this.fiscalShift.changedAt = response.changedAt || null
        this.updateFiscalShiftWarning()
      },
      applySettlementInfo (response) {
        this.fiscalShift.terminalSettlementRequired = Boolean(response.terminalSettlementRequired)
        this.fiscalShift.lastTerminalSettlementAt = response.lastTerminalSettlementAt || null
      },
      // дата смены приходит днём филиала: сравниваем с его «сегодня», а не с днём браузера
      filialToday () {
        return todayInTimeZone(this.localStorageBranch?.timeZone || null)
      },
      updateFiscalShiftWarning () {
        const openedInPast =
          this.fiscalShift.status === FiscalShiftStatus.Open &&
          this.fiscalShift.date !== this.filialToday()

        this.fiscalShiftWarning.show = openedInPast
        if (openedInPast) {
          this.setFiscalShiftStatusWarningText(this.fiscalShift.date)
        }
      },
      startShiftPolling () {
        this.stopShiftPolling()
        this.shiftStatusLoading = true
        this.shiftPollStartedAt = Date.now()
        this.shiftPollTimer = setInterval(this.pollShiftStatus, 2000)
      },
      stopShiftPolling () {
        if (this.shiftPollTimer) {
          clearInterval(this.shiftPollTimer)
          this.shiftPollTimer = null
        }
      },
      async syncShiftState () {
        try {
          await fiscalShiftApi.sync(this.localStorageBranch.id)
        } catch (e) {
          return
        }

        // Сверка с ККТ асинхронна и идёт в фоне, без прелоадера: поллим статус,
        // пока бек отвечает syncing=true (активная команда GetDataKKT).
        let attempts = 0
        this.stopSyncPolling()
        this.syncPollTimer = setInterval(async () => {
          attempts += 1
          if (attempts > 6) {
            this.stopSyncPolling()
            return
          }

          try {
            const response = await fiscalShiftApi.fetchStatus(this.localStorageBranch.id)

            if (response.pending) {
              // идёт кнопочная операция - её ведёт основной поллинг
              this.stopSyncPolling()
              this.startShiftPolling()
              return
            }

            if (!response.syncing) {
              this.applyShiftResponse(response)
              this.stopSyncPolling()
            }
          } catch (e) {
            // фоновое обновление - ошибки не показываем
          }
        }, 3000)
      },
      stopSyncPolling () {
        if (this.syncPollTimer) {
          clearInterval(this.syncPollTimer)
          this.syncPollTimer = null
        }
      },
      async pollShiftStatus () {
        // страховочный таймаут фронта поверх бек-таймаутов ККМ-канала
        if (Date.now() - this.shiftPollStartedAt > 120000) {
          this.stopShiftPolling()
          this.shiftStatusLoading = false
          this.showErrorMessage('Не удалось получить статус операции, обновите страницу')
          return
        }

        try {
          const response = await fiscalShiftApi.fetchStatus(this.localStorageBranch.id)

          if (response.pending) {
            return
          }

          this.stopShiftPolling()
          this.applyShiftResponse(response)
          this.shiftStatusLoading = false

          if (response.error) {
            this.showErrorMessage(response.error)
          } else {
            this.showSuccessMessage('Операция со сменой выполнена')
          }
        } catch (e) {
          // сетевые сбои игнорируем до истечения таймаута поллинга
        }
      },
      updateShiftStatus (data) {
        // вещание несёт только состояние смены, поля сверки в нём — значения по умолчанию
        this.applyShiftState(data)
      },
      async openFiscalShift () {
        if (!this.userCanChangeShiftStatus) {
          this.showErrorMessage('Доступ запрещен')
          return
        }

        this.shiftStatusLoading = true
        try {
          const response = await fiscalShiftApi.open(this.localStorageBranch.id)
          this.shiftStatusDialog = false

          if (response.pending) {
            this.startShiftPolling()
          } else {
            this.applyShiftResponse(response)
            this.shiftStatusLoading = false
            this.showSuccessMessage('Смена успешно открыта')
          }
        } catch (e) {
          this.shiftStatusLoading = false
          this.showErrorFromResponse(e)
        }
      },
      async closeFiscalShift () {
        if (!this.userCanChangeShiftStatus) {
          this.showErrorMessage('Доступ запрещен')
          return
        }

        this.shiftStatusLoading = true
        try {
          const response = await fiscalShiftApi.close(this.localStorageBranch.id, {
            skipTerminalSettlement: this.skipTerminalSettlement,
          })
          this.shiftStatusDialog = false

          if (response.pending) {
            this.startShiftPolling()
          } else {
            this.applyShiftResponse(response)
            this.shiftStatusLoading = false
            this.showSuccessMessage('Смена успешно закрыта')
          }

          // сверка идёт параллельно с фискальным закрытием, не ждём
          this.handleAutoSettlement(response)
        } catch (e) {
          this.shiftStatusLoading = false
          this.showErrorFromResponse(e)
        }
      },
      async handleAutoSettlement (response) {
        const notice = autoSettlementNotice(response)

        if (!notice) {
          return
        }

        if (notice.isError) {
          this.settlementNotice = notice
          this.showErrorMessage(notice.text)
          return
        }

        // уже идёт ожидание этой сверки (запущена из меню «Терминал»)
        if (this.settlementWatcher) {
          return
        }

        await this.watchSettlementResult(response.terminalSettlement)
      },
      async showFiscalShiftDialog () {
        this.shouldOpenFiscalShift = this.fiscalShift.status === FiscalShiftStatus.Closed
        this.skipTerminalSettlement = false

        // предупреждение о сверке — по свежему статусу: её могли сделать из меню или в другой вкладке
        if (!this.shouldOpenFiscalShift) {
          try {
            this.applyShiftResponse(await fiscalShiftApi.fetchStatus(this.localStorageBranch.id))
          } catch (e) {
            // покажем диалог по тому, что знаем
          }
        }

        this.shiftStatusDialog = true
      },
      setFiscalShiftStatusWarningText (date) {
        const formattedDate = new Date(date).toLocaleString(this.$i18n.locale, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })

        this.fiscalShiftWarning.text = `Не закрыта касса за ${formattedDate}`
      },
      async closeCardShift () {
        if (!this.isKkmServerPaymentEnabled) {
          this.showErrorMessage('Филиал не использует терминал')
          return
        }

        this.isCardShiftClosing = true

        try {
          const settlement = await terminalSettlementApi.start(this.localStorageBranch.id)
          await this.watchSettlementResult(settlement)
        } catch (e) {
          this.showErrorMessage(this.getErrorMessageFromResponse(e) || 'Не удалось выполнить сверку итогов')
        } finally {
          this.isCardShiftClosing = false
        }
      },
      async watchSettlementResult (settlement) {
        this.isCardShiftClosing = true
        this.settlementWatcher = watchSettlement(this.localStorageBranch.id, settlement)
        this.settlementNotice = {
          isError: false,
          text: 'Идёт сверка терминала. Не закрывайте CRM, пока она не завершится',
        }

        try {
          const result = await this.settlementWatcher.promise

          if (result.status === 'success') {
            this.settlementNotice = null
            this.showSuccessMessage('Сверка итогов выполнена')
            await this.refreshSettlementInfo()
          } else {
            this.failSettlement(result.error || 'Сверка итогов не выполнена')
          }
        } catch (e) {
          this.failSettlement(this.getErrorMessageFromResponse(e) || 'Не удалось выполнить сверку итогов')
        } finally {
          this.settlementWatcher = null
          this.isCardShiftClosing = false
        }
      },
      failSettlement (message) {
        this.settlementNotice = {
          isError: true,
          text: `${message}. Повторите сверку из меню «Терминал»`,
        }
        this.showErrorMessage(message)
      },
      async refreshSettlementInfo () {
        try {
          const response = await fiscalShiftApi.fetchStatus(this.localStorageBranch.id)
          this.applySettlementInfo(response)
        } catch (e) {
          // строка «Последняя сверка» обновится при следующем чтении статуса
        }
      },
      async getTerminalReport (detailed) {
        if (this.terminalReportLoading) return

        if (!this.isKkmServerPaymentEnabled) {
          this.showErrorMessage('Филиал не использует терминал')
          return
        }

        const filialId = this.localStorageBranch.id
        this.terminalReportLoading = detailed ? 'full' : 'short'

        try {
          const { operationId, timeoutSeconds } = await terminalReportApi.start(filialId, detailed)
          if (this._isDestroyed) return
          this.terminalReportWatcher = watchTerminalCommand(
            () => terminalReportApi.fetch(filialId, operationId),
            timeoutSeconds,
            'Результат отчёта пока неизвестен, проверьте терминал',
          )

          const result = await this.terminalReportWatcher.promise

          if (result.status === 'success') {
            this.showSuccessMessage('Итоги дня напечатаны')
          } else {
            this.showErrorMessage(result.error || 'Не удалось получить итоги дня')
          }
        } catch (e) {
          this.showErrorMessage(this.getErrorMessageFromResponse(e) || 'Не удалось получить итоги дня')
        } finally {
          this.terminalReportWatcher = null
          this.terminalReportLoading = null
        }
      },
    },
  }
</script>

<style lang="less" scoped>
.top-buttons-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media (max-width: 1200px) {
    flex-direction: column;
    justify-content: flex-start;
  }
}

.top-btn {
  @media (max-width: 1200px) {
    width: 145px;
  }
}

.terminal-btn {
  margin-left: 12px;

  @media (max-width: 1200px) {
    margin-top: 8px;
    margin-left: 0;
  }
}

.terminal-menu_hint {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  text-align: center;
}

.settlement-warning {
  margin-top: 16px;
}

// v-input — блочный flex на всю ширину: inline-flex сжимает чекбокс по содержимому, и он центрируется вместе с текстом.
// Шрифт и цвет подписи — от подзаголовка диалога, а не свои у Vuetify
.settlement-warning .settlement-warning_checkbox {
  display: inline-flex;
  margin-top: 16px;
  font-size: inherit;
  color: inherit;

  ::v-deep .v-label {
    font-size: inherit;
    color: inherit;
  }
}

.shift-status-label {
  font-size: 18px;

  @media (max-width: @sm) {
    font-size: 14px;
  }
}

.connector-indicators-wrapper {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 575px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.cashier-component {
  min-height: calc(100vh - 250px);
  position: relative;

  @media (max-width: @sm) {
    font-size: 14px;
  }

  //.row {
  //  margin-right: -6px;
  //  margin-left: -6px;
  //  .col {
  //    padding-left: 6px;
  //    padding-right: 6px;
  //  }
  //}
  .left-side {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .label {
      min-height: 25px;
    }
    .row {
      .col {
        display: flex;
        flex-direction: column;
        justify-content: stretch;
      }
    }
    .stat-item {
      flex-grow: 1;
    }
  }

  .page-title {
    margin: 0;
    font-size: 24px;
    line-height: 33px;
    font-weight: 500;
  }

  ::v-deep {
    .stat-item {
      border-radius: 10px;
      padding: 20px 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      background-color: #eff0f4;

      &.icon {
        position: relative;
        z-index: 0;

        svg {
          position: absolute;
          right: 15px;
          top: 0;
          bottom: 0;
          margin: auto;
          z-index: -1;
        }
      }

      &.stat-item-total-today {
        svg {
          @media (max-width: @sm) {
            opacity: 0.3;
          }
        }
      }

      &.lg {
        min-height: 162px;
      }

      &.md {
        min-height: 130px;
      }

      &.sm {
        min-height: 96px;
      }

      .value {
        white-space: nowrap;
        display: block;
        font-weight: 500;
        font-size: 24px;
        @media (max-width: @sm) {
          font-size: 18px;
          font-weight: 600;
        }

        &.income-today-value {
          font-size: 43px;
          color: #6bad00;
          @media (max-width: @sm) {
            font-size: 24px;
          }
        }

        &.total {
          color: #e91e63;
        }
      }
    }
  }

  .label {
    display: block;
    margin-bottom: 10px;
    font-size: 16px;

    @media (max-width: @sm) {
      font-size: 14px;
      line-height: 1;
    }
  }

  .debtor-alert {
    display: flex;
    align-items: center;
    background-color: #fff3d1;
    padding: 15px 20px;
    border-radius: 5px;
    margin-bottom: 10px;

    @media (max-width: 1200px) {
      padding: 10px 15px;
    }

    svg {
      margin-right: 16px;
      width: 20px;
      height: 20px;
    }
    span {
      font-size: 16px;
      line-height: 23px;
    }
  }

  .fullheight {
    height: 100%;
  }

  .cashier-section {
    background-color: @color-secondary;
    padding: 15px 15px;
    border-radius: 10px;
  }

  .td-total {
    background-color: papayawhip;
  }

  .td-debtor {
    background-color: pink;
  }

  td {
    border: 1px solid rgba(0, 0, 0, 0.3);
    padding: 10px !important;

    span {
      font-size: 18px;

      &.total-today {
        font-size: 24px;
      }
    }
  }
  .debtor-totals-col {
    display: flex;
    flex-direction: column;
    justify-content: stretch;

    .flex-column {
      flex-grow: 1;
    }
  }
}
::v-deep {
  .v-data-table {
    @media (max-width: 600px) {
      thead {
        display: none;
      }
    }
  }
  .v-data-table tr:nth-of-type(even) td {
    background-color: rgba(0, 0, 0, 0.03);
  }
  .v-data-table tr,
  .v-data-table tr td {
    border: none !important;
  }
  .v-data-table tr th {
    font-weight: 500 !important;
    color: #000 !important;
    background: #f1f3f6 !important;
    white-space: nowrap;
    line-height: 1.1 !important;
    padding-bottom: 10px !important;

    span {
      white-space: pre-wrap;
    }
  }
}
</style>
